"use client";

import { Fragment, ReactNode, useEffect, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import Counter from "components/counter/Counter";
import CustomSwitch from "components/form/CustomSwitch";
import { isSpringBreakDealsRoute } from "components/templates/HomePage/SearchSection/springBreakIntent";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markMultiUnitStore } from "stores/multi-unit-detail/multiUnitSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";
export interface SearchGuestPopoverType {
  children: (x: {
    adult: number;
    child: number;
    pets: boolean;
  }) => ReactNode | string;
  initalState?: {
    adultCount: number;
    childCount: number;
    petsAllowed: boolean;
  };
  isDetailPage?: boolean;
  onChange?: (x: {
    adult: number;
    child: number;
    pets: boolean;
  }) => void;
  isShowPopover?: boolean;
  popoverStyle?: string;
}
const SearchGuestPopoverInVacationRental = (props: SearchGuestPopoverType) => {
  // pass the button to this component as a children to trigger popover.
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const pathname = usePathname();
  const isSpringBreakRoute = isSpringBreakDealsRoute(pathname);
  const {
    children,
    onChange,
    initalState,
    isDetailPage = false,
    isShowPopover = currentSearchTab === "guest",
    popoverStyle = ""
  } = props;
  const dispatch = useAppDispatch();
  const [guestOptions, setGuestOptions] = useState({
    adult: initalState?.adultCount || 0,
    child: initalState?.childCount || 0,
    pets: initalState?.petsAllowed || false
  });
  useEffect(() => {
    setGuestOptions({
      adult: initalState?.adultCount || 0,
      child: initalState?.childCount || 0,
      pets: initalState?.petsAllowed || false
    });
  }, [initalState?.adultCount, initalState?.childCount, initalState?.petsAllowed]);
  const handleSearchFlow = (newCurrentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab: newCurrentSearchTab
    }));
  };
  const t = useTranslations("common");
  return <Popover as='div' className='relative h-full' data-sentry-element="Popover" data-sentry-component="SearchGuestPopoverInVacationRental" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx">
      <div className='h-full w-full'>{children(guestOptions)}</div>
      <Transition as={Fragment} show={isShowPopover} data-sentry-element="Transition" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx">
        <div>
          <Popover.Overlay className='fixed inset-0 bg-transparent' onClick={() => {
          handleSearchFlow(null);
          dispatch(markMultiUnitStore({
            isMultiUnitGuestPopoverOpen: false
          }));
        }} data-sentry-element="Popover.Overlay" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx" />
          <Popover.Panel id='GuestFilter' className={`absolute left-0 top-[84px] ${isSpringBreakRoute ? "!z-[1300]" : "z-[12!important]"} w-[282px] max-w-[282px] rounded-2xl border border-inherit bg-offWhite shadow-[0px_1px_2px_0px_#0000000d] md:left-[-28%] mxd:left-0 ${popoverStyle}`} data-sentry-element="Popover.Panel" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx">
            <main>
              <div className='flex flex-col gap-6 p-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='mb-[3px] text-base font-semibold leading-4 text-brand-dark'>
                      {/* Adults */}
                      {t("words.adults")}
                    </p>
                    <span className='text-medium text-sm text-Gray-700'>
                      Ages 13 and above
                      {/*{t("words.ages13orabove")}*/}
                      {/* Ages 13 or above */}
                    </span>
                  </div>
                  <div>
                    <Counter key='Adults' isDetailPage={isDetailPage} value={guestOptions.adult} decrementCounter={() => {
                    setGuestOptions(_guestOptions => {
                      const updatedG = {
                        ..._guestOptions,
                        adult: isDetailPage && _guestOptions.adult === 1 ? _guestOptions.adult : _guestOptions.adult - 1
                      };
                      if (onChange) {
                        onChange(updatedG);
                      }
                      return updatedG;
                    });
                  }} incrementCounter={() => {
                    setGuestOptions(_guestOptions => {
                      const updatedG = {
                        ..._guestOptions,
                        adult: _guestOptions.adult + 1
                      };
                      if (onChange) {
                        onChange(updatedG);
                      }
                      return updatedG;
                    });
                  }} isIncrementDisabled={guestOptions.adult + guestOptions.child >= 20} max={20} data-sentry-element="Counter" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx" />
                  </div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='mb-[3px] text-base font-semibold leading-4 text-brand-dark'>
                      {/* Children */}
                      {t("words.children")}
                    </p>
                    <span className='text-medium text-sm text-Gray-700'>
                      Ages 12 and under
                      {/*{t("words.ages2_12")}*/} {/* Ages 2-12 */}
                    </span>
                  </div>
                  <div>
                    <Counter key='Children' value={guestOptions.child} decrementCounter={() => {
                    setGuestOptions(_guestOptions => {
                      const updatedG = {
                        ..._guestOptions,
                        child: _guestOptions.child - 1
                      };
                      if (onChange) {
                        onChange(updatedG);
                      }
                      return updatedG;
                    });
                  }} incrementCounter={() => {
                    setGuestOptions(_guestOptions => {
                      const updatedG = {
                        ..._guestOptions,
                        child: _guestOptions.child + 1
                      };
                      if (onChange) {
                        onChange(updatedG);
                      }
                      return updatedG;
                    });
                  }} isIncrementDisabled={guestOptions.adult + guestOptions.child >= 20} max={20} data-sentry-element="Counter" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx" />
                  </div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='mb-[3px] text-base font-semibold leading-4 text-brand-dark'>
                      {/* Pets */}
                      {t("words.pets")}
                    </p>
                    <span className='text-medium text-sm text-Gray-700'>
                      {t("words.arepetsallowed")}
                      {/* Are pets allowed? */}
                    </span>
                  </div>
                  <CustomSwitch id='Pets' checked={guestOptions.pets} onChange={() => {
                  setGuestOptions(_guestOptions => {
                    const updatedG = {
                      ..._guestOptions,
                      pets: !_guestOptions.pets
                    };
                    if (onChange) {
                      onChange(updatedG);
                    }
                    return updatedG;
                  });
                }} data-sentry-element="CustomSwitch" data-sentry-source-file="SearchGuestPopoverInVacationRental.tsx" />
                </div>
              </div>
            </main>
          </Popover.Panel>
        </div>
      </Transition>
    </Popover>;
};
export default SearchGuestPopoverInVacationRental;